import { useEffect, useState } from "react";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, Sparkles, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/** Renders a 1-5 star input. `value` is the current rating; `onChange`
 *  is called with the newly selected integer. Read-only when `readonly=true`. */
function StarRow({ value, onChange, readonly = false, size = "w-5 h-5", testid }) {
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readonly}
          onClick={() => onChange?.(n)}
          className={`${readonly ? "cursor-default" : "cursor-pointer hover:scale-110 transition-transform"}`}
          data-testid={testid ? `${testid}-star-${n}` : undefined}
          aria-label={`Rate ${n} of 5`}
        >
          <Star
            className={`${size} ${
              n <= value
                ? "fill-[#D4AF37] text-[#D4AF37]"
                : "text-slate-300"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

function ReviewCard({ review, canDelete, onDelete }) {
  const initials = (review.guest_name || "G")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`review-${review.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-semibold text-sm shrink-0">
            {initials}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-[#0A1128]">{review.guest_name}</span>
              {review.verified && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] tracking-[0.16em] uppercase text-emerald-700 font-semibold"
                  data-testid="review-verified-badge"
                >
                  <ShieldCheck className="w-2.5 h-2.5" /> Verified Guest
                </span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-3">
              <StarRow value={review.rating} readonly size="w-3.5 h-3.5" />
              <span className="text-xs text-slate-500">
                Freshness <b className="text-[#0A1128]">{review.freshness_rating}/5</b>
              </span>
            </div>
          </div>
        </div>
        {canDelete && (
          <button
            onClick={() => onDelete(review.id)}
            className="text-slate-400 hover:text-rose-600 transition-colors"
            data-testid="review-delete"
            aria-label="Delete review"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      {review.text && (
        <p className="mt-4 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
          {review.text}
        </p>
      )}
      <div className="mt-3 text-[11px] text-slate-400">
        {new Date(review.created_at).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })}
      </div>
    </div>
  );
}

export default function PropertyReviews({ propertyId, propertyStatus }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [rating, setRating] = useState(5);
  const [freshness, setFreshness] = useState(5);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/reviews`);
      setData(data);
      if (data.my_review) {
        setRating(data.my_review.rating);
        setFreshness(data.my_review.freshness_rating);
        setText(data.my_review.text || "");
      }
    } catch {
      setData({ aggregate: { count: 0, avg_rating: null, avg_freshness: null }, reviews: [], my_review: null });
    }
  };

  useEffect(() => { load(); }, [propertyId]);

  const canReview = user?.role === "guest" && propertyStatus === "certified";
  const hasReviewed = !!data?.my_review;

  const submit = async () => {
    if (!canReview) return;
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        property_id: propertyId,
        rating,
        freshness_rating: freshness,
        text,
      });
      toast.success(hasReviewed ? "Review updated" : "Review posted — thank you!");
      await load();
    } catch (e) {
      toast.error(fmtErr(e) || "Could not save review");
    } finally {
      setSubmitting(false);
    }
  };

  const removeReview = async (rid) => {
    if (!window.confirm("Delete your review? This cannot be undone.")) return;
    try {
      await api.delete(`/reviews/${rid}`);
      toast.success("Review deleted");
      setRating(5); setFreshness(5); setText("");
      await load();
    } catch (e) {
      toast.error(fmtErr(e) || "Could not delete review");
    }
  };

  if (!data) return null;
  const agg = data.aggregate;

  return (
    <section className="mt-14" data-testid="property-reviews">
      {/* Header + aggregate */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs tracking-[0.28em] uppercase text-emerald-700 font-semibold">
            Verified Guest Reviews
          </div>
          <h2 className="mt-2 font-serif text-3xl md:text-4xl text-[#0A1128] tracking-tight">
            What guests actually experienced
          </h2>
        </div>
        {agg.count > 0 && (
          <div className="flex items-center gap-6" data-testid="reviews-aggregate">
            <div className="text-right">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-[#D4AF37] text-[#D4AF37]" />
                <span className="font-serif text-3xl text-[#0A1128]" data-testid="reviews-avg-rating">
                  {agg.avg_rating?.toFixed(1)}
                </span>
              </div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-slate-500 mt-1">
                {agg.count} review{agg.count === 1 ? "" : "s"}
              </div>
            </div>
            <div className="w-px h-10 bg-slate-300" />
            <div className="text-right">
              <div className="flex items-center gap-2 justify-end">
                <Sparkles className="w-5 h-5 text-emerald-600" />
                <span className="font-serif text-3xl text-[#0A1128]" data-testid="reviews-avg-freshness">
                  {agg.avg_freshness?.toFixed(1)}
                </span>
              </div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-slate-500 mt-1">
                avg freshness
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Review form or gated CTA */}
      {canReview ? (
        <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-6" data-testid="review-form">
          <div className="text-sm text-slate-600 mb-4">
            {hasReviewed ? (
              <>You&apos;ve reviewed this stay. Update your review any time.</>
            ) : (
              <>Post a verified guest review — only signed-in guests can leave one.</>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500 font-semibold mb-2">
                Overall
              </div>
              <StarRow value={rating} onChange={setRating} testid="review-rating" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500 font-semibold mb-2">
                Freshness on arrival
              </div>
              <StarRow value={freshness} onChange={setFreshness} testid="review-freshness" />
            </div>
          </div>
          <Textarea
            className="mt-5 rounded-2xl min-h-[110px]"
            placeholder="How did the space actually smell and feel? Any allergens, air quality issues, or standout freshness moments?"
            maxLength={1000}
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="review-text"
          />
          <div className="mt-2 text-[11px] text-slate-400 text-right">{text.length}/1000</div>
          <div className="mt-4 flex justify-end">
            <Button
              onClick={submit}
              disabled={submitting}
              className="rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white px-8"
              data-testid="review-submit"
            >
              {submitting ? "Saving…" : hasReviewed ? "Update review" : "Post review"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-8 rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-600" data-testid="review-gated">
          {!user ? (
            <>
              <Link to="/login" className="text-emerald-700 hover:underline font-medium">Sign in</Link> or{" "}
              <Link to="/register" className="text-emerald-700 hover:underline font-medium">create a guest account</Link>{" "}
              to leave a verified freshness review.
            </>
          ) : user.role !== "guest" ? (
            <>Only guest accounts can post reviews. Sign in with a guest account to review this stay.</>
          ) : propertyStatus !== "certified" ? (
            <>Reviews are only accepted on certified stays.</>
          ) : null}
        </div>
      )}

      {/* Review list */}
      <div className="mt-8 grid md:grid-cols-2 gap-4" data-testid="reviews-list">
        {data.reviews.length === 0 ? (
          <div className="md:col-span-2 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500" data-testid="reviews-empty">
            No verified reviews yet. Be the first.
          </div>
        ) : (
          data.reviews.map((r) => (
            <ReviewCard
              key={r.id}
              review={r}
              canDelete={user && (user.id === r.user_id || user.role === "admin")}
              onDelete={removeReview}
            />
          ))
        )}
      </div>
    </section>
  );
}
